import { Avatar, Clickable, Col, Divider, Icon, Row, Sidepanel, Spinner2, Text } from 'components';
import { Api, theme } from 'lib';
import { useEffect, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import styled from 'styled-components';
import { GameDetailModal, GameItem } from '../Games/components';

const SelectContainer = styled.div`
  position: relative;
  height: 36px;
  border-radius: 50px;
  border: 1px solid ${theme.GREY_50};
  background: white;
  display: flex;
  align-items: center;
  padding: 0 14px;
  cursor: pointer;
  transition: border 0.15s ease;

  &:hover {
    border-color: ${theme.GREY_60};
  }
`;

const StyledSelect = styled.select`
  height: 100%;
  border: none;
  background: transparent;
  font-family: CeraPro;
  font-size: 14px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 20px;

  &:focus {
    outline: none;
  }
`;

const SelectArrow = styled.div`
  position: absolute;
  right: 10px;
  pointer-events: none;
  color: ${theme.GREY_60};
  display: flex;
  align-items: center;
`;

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Games' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'in-play', label: 'In Play' },
  { value: 'finished', label: 'Finished' }
];

const FILTER_PARAMS = {
  all: { sortBy: { date: -1 } },
  upcoming: { status: 'scheduled', sortBy: { date: 1 } },
  'in-play': { status: 'in-play', sortBy: { date: -1 } },
  finished: { status: 'finished', sortBy: { date: -1 } }
};

const GamesPanel = ({ visible, closeModal, team }) => {
  const [games, setGames] = useState([]),
    [loading, setLoading] = useState(true),
    [total, setTotal] = useState(0),
    [predictionStats, setPredictionStats] = useState(null),
    [selectedGame, setSelectedGame] = useState(null),
    [filter, setFilter] = useState('all');

  useEffect(() => {
    if (!visible || !team?._id) return;

    fetchGames(filter);
  }, [visible, team?._id, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deliberately not keyed on `filter` — the prediction total covers every game the
  // team has ever played, so switching the status dropdown must not refetch it.
  useEffect(() => {
    if (!visible || !team?._id) return;

    fetchPredictionStats();
  }, [visible, team?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchGames(activeFilter) {
    setLoading(true);

    const { status, sortBy } = FILTER_PARAMS[activeFilter] || FILTER_PARAMS.all;

    const res = await Api.post('/admin/games/get', {
      teamId: team._id,
      ...(status && { status }),
      sortBy,
      limit: 100
    });

    if (!res) {
      setLoading(false);
      return;
    }

    setGames(res.games || []);
    setTotal(res.total || 0);
    setLoading(false);
  }

  async function fetchPredictionStats() {
    const res = await Api.get(`/admin/predictions/stats?teamId=${team._id}`);

    // Tolerates the varying response envelopes the admin API returns, the same way
    // GameDetailModal's getGroups does.
    const stats = res?.data?.data || res?.data || res;

    setPredictionStats(stats?.totalVotes == null ? null : stats);
  }

  const handleGameClick = (game) => {
    setSelectedGame(game);
  };

  const handleCloseGameModal = () => {
    setSelectedGame(null);
  };

  return (
    <Sidepanel pad="24px 0" wid={800} visible={visible} closeModal={closeModal}>
      <Col>
        <Row pad="0 24px 24px" wid="calc(100% - 48px)" start noFlex>
          <Col center>
            <Clickable onClick={closeModal}>
              <Icon type="arrowLeft" dimensions={20} marg="0 12px 0 0" />
            </Clickable>
          </Col>

          <Avatar user={team} wid="100%" />

          <Row between wid="100%" ht="100%">
            <Col marg="0 0 0 10px" between ht="100%">
              <Col noFlex>
                <Text med>{team?.name}</Text>
                <Text c1 med col={theme.GREY_60}>
                  @{team?.username}
                </Text>
              </Col>
            </Col>

            <Text b2>{team?.sport && team.sport + ' club'}</Text>
          </Row>
        </Row>

        <Divider />

        <Row pad="12px 24px" between wid="calc(100% - 48px)" noFlex>
          <Row center gap="8px">
            <Text b2 col={theme.GREY_60}>
              {total} games
            </Text>
            {predictionStats && (
              <>
                <Text b2 col={theme.GREY_50}>
                  ·
                </Text>
                <Text b2 bold col={theme.SECONDARY}>
                  {predictionStats.totalVotes} predictions
                </Text>
              </>
            )}
          </Row>
          <SelectContainer>
            <StyledSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
              {FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </StyledSelect>
            <SelectArrow>
              <FiChevronDown size={14} />
            </SelectArrow>
          </SelectContainer>
        </Row>

        {loading ? (
          <Col ht="100%" center pad="24px 0">
            <Spinner2 />
          </Col>
        ) : (
          <Col pad="0 24px 24px" start wid="100%">
            {games.length === 0 ? (
              <Col center pad="40px 0">
                <Text b2 col={theme.GREY_60}>
                  No games found
                </Text>
              </Col>
            ) : (
              <Col noFlex wid="100%" gap="12px">
                {games.map((game) => (
                  <GameItem
                    key={game._id}
                    game={game}
                    teamId={team._id}
                    onClick={handleGameClick}
                  />
                ))}
              </Col>
            )}
          </Col>
        )}
      </Col>

      <GameDetailModal
        visible={!!selectedGame}
        closeModal={handleCloseGameModal}
        game={selectedGame}
        teamId={team._id}
      />
    </Sidepanel>
  );
};

export default GamesPanel;
